import { Typography, IconButton, useTheme, useMediaQuery } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import Box from "@mui/material/Box";
import React, { useState, useCallback, useEffect } from "react";
import { useMode } from "../../mode/ModeContext";
import { modes } from "../../mode/modes";
import { loadSessionHistory } from "../../utils/localStorage";
import ModeCard from "./ModeCard";
import ModeHeader from "./ModeHeader";
import SessionCarousel from "./SessionCarousel";

interface ModeSwitcherProps {
  showAdd?: boolean;
  showEdit?: boolean;
  showCancel?: boolean;
  toggleAdd?: () => void;
  toggleEdit?: () => void;
  toggleCancel?: () => void;
}

export default function ModeSwitcher({
  showAdd,
  showEdit,
  showCancel,
  toggleAdd,
  toggleEdit,
  toggleCancel,
}: ModeSwitcherProps) {
  const { mode, setMode } = useMode();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Session history for the explore page carousel
  const [sessions, setSessions] = useState(() => loadSessionHistory());
  const refreshSessions = useCallback(() => {
    setSessions(loadSessionHistory());
  }, []);

  // Refresh sessions when returning to the explore page
  useEffect(() => {
    if (mode === null) {
      refreshSessions();
    }
  }, [mode, refreshSessions]);

  // Show compact version when mode is set (manual or special modes)
  if (mode !== null) {
    const currentMode = modes[mode];

    return (
      <Box sx={{ px: 2, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <ModeHeader
            label={currentMode.label}
            description={currentMode.description}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              ...(isMobile && {
                position: "fixed",
                bottom: 16,
                right: 16,
                flexDirection: "column",
                zIndex: 1000,
              }),
            }}
          >
            {showAdd && toggleAdd && (
              <IconButton
                aria-label="add"
                size="large"
                color="primary"
                onClick={toggleAdd}
                sx={
                  isMobile
                    ? {
                        backgroundColor: "primary.main",
                        color: "white",
                        "&:hover": {
                          backgroundColor: "primary.dark",
                        },
                        boxShadow: 3,
                      }
                    : {}
                }
              >
                <AddIcon fontSize="inherit" />
              </IconButton>
            )}
            {showEdit && toggleEdit && (
              <IconButton
                aria-label="edit"
                size="large"
                color="primary"
                onClick={toggleEdit}
                sx={
                  isMobile
                    ? {
                        backgroundColor: "primary.main",
                        color: "white",
                        "&:hover": {
                          backgroundColor: "primary.dark",
                        },
                        boxShadow: 3,
                      }
                    : {}
                }
              >
                <EditIcon fontSize="inherit" />
              </IconButton>
            )}
            {showCancel && toggleCancel && (
              <IconButton
                aria-label="cancel"
                size="large"
                color="primary"
                onClick={toggleCancel}
                sx={
                  isMobile
                    ? {
                        backgroundColor: "primary.main",
                        color: "white",
                        "&:hover": {
                          backgroundColor: "primary.dark",
                        },
                        boxShadow: 3,
                      }
                    : {}
                }
              >
                <CancelIcon fontSize="inherit" />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  // Default view: Show mode selection grid when mode is null
  return (
    <Box sx={{ px: 2 }}>
      <Typography variant="h5" component={"h1"} sx={{ mb: 2 }}>
        What do you want to explore?
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Each mode lets you compare mortgage scenarios side by side, showing how
        changes to one factor affect payments and total cost. You can also
        include savings and investment accounts.
      </Typography>
      <SessionCarousel
        sessions={sessions}
        onSessionsChanged={refreshSessions}
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(3, 1fr)",
          },
          gap: 3,
          gridAutoRows: "1fr",
        }}
      >
        {Object.entries(modes).map(([key, modeDefinition]) => (
          <ModeCard
            key={key}
            modeKey={key}
            mode={modeDefinition}
            onSelect={() => setMode(key as keyof typeof modes)}
          />
        ))}
      </Box>
    </Box>
  );
}
